import { FormEvent, useState } from 'react';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { ACCESS_GOVERNANCE, EARLY_ACCESS_BADGE } from './marketingConfig';

interface AccessRequestRecord {
  id: string;
  cohort: string;
  onboardingWave: string;
}

interface AccessRequestSubmissionResponse {
  data: {
    id: string;
    cohort: string;
    onboardingWave: string;
    status: string;
  } | null;
  error: { message: string } | null;
}

async function submitAccessRequest(payload: Record<string, string>): Promise<AccessRequestRecord> {
  const response = await fetch('/api/access-requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  let result: AccessRequestSubmissionResponse | null = null;
  try {
    result = (await response.json()) as AccessRequestSubmissionResponse;
  } catch {
    throw new Error('Unable to process access request response. Please try again shortly.');
  }

  const errorMessage =
    result?.error?.message
    ?? (response.ok ? null : 'Unable to submit access request right now. Please try again shortly.');

  if (!response.ok || !result?.data || errorMessage) {
    throw new Error(errorMessage ?? 'Unable to submit access request right now. Please try again shortly.');
  }

  return {
    id: result.data.id,
    cohort: result.data.cohort,
    onboardingWave: result.data.onboardingWave,
  };
}

const SPORT_OPTIONS = [
  'Baseball', 'Football', 'Basketball', 'Hockey', 'Soccer', 'Tennis',
  'MMA', 'Cricket', 'Formula 1', 'Golf', 'Motorsport', 'Esports', 'Boxing',
  'Horse Racing', 'Athletics', 'Australian Rules', 'Breaking', 'Figure Skating',
  'Gymnastics', 'Lacrosse', 'Rowing', 'Rugby League', 'Volleyball', 'Sailing',
  'Strength Sports', 'Winter Sports',
];

const BETTING_FREQUENCY_OPTIONS = [
  'Daily',
  'A few times a week',
  'Weekly',
  'A few times a month',
  'Occasionally',
];

export function AccessRequestPage() {
  usePageMeta({
    title: 'Request Access | Navyrx Sports Controlled Rollout',
    description:
      'Request controlled early access to Navyrx Sports. Access requests are reviewed in onboarding waves to preserve operational quality and calibration.',
    path: '/access',
  });

  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [sportInterest, setSportInterest] = useState('');
  const [userType, setUserType] = useState('Casual');
  const [bettingFrequency, setBettingFrequency] = useState('');
  const [intent, setIntent] = useState('');
  const [useCase, setUseCase] = useState('');
  const [bankrollRange, setBankrollRange] = useState('');
  const [professionalBackground, setProfessionalBackground] = useState('');
  const [favoriteSportsbook, setFavoriteSportsbook] = useState('');
  const [analyticsLevel, setAnalyticsLevel] = useState('');
  const [preferredBetType, setPreferredBetType] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<AccessRequestRecord | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (ACCESS_GOVERNANCE.inviteOnly && !inviteCode.trim()) {
      setError('An invite code is currently required for this rollout phase.');
      return;
    }

    setIsSubmitting(true);

    try {
      const savedRequest = await submitAccessRequest({
        displayName: displayName.trim(),
        fullName: fullName.trim(),
        email: email.trim(),
        sportInterest: sportInterest.trim(),
        userType,
        bettingFrequency,
        intent: intent.trim(),
        useCase: useCase.trim(),
        bankrollRange: bankrollRange.trim(),
        professionalBackground: professionalBackground.trim(),
        favoriteSportsbook: favoriteSportsbook.trim(),
        analyticsLevel: analyticsLevel.trim(),
        preferredBetType: preferredBetType.trim(),
        inviteCode: inviteCode.trim(),
        _hp: honeypot,
      });
      setSubmitted(savedRequest);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'Unable to submit access request right now. Please try again shortly.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mx-auto max-w-4xl px-4 pb-16 pt-14 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-8">
          <p className="inline-flex items-center gap-2 rounded-full border border-emerald-300/30 bg-emerald-300/10 px-3 py-1 text-xs text-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5" /> Access Request Received
          </p>
          <h1 className="mt-5 text-3xl font-semibold">Your request has been added to the controlled rollout queue.</h1>
          <p className="mt-4 text-sm text-gray-300">
            Navyrx Pulse is currently onboarding a limited number of early-access users during the institutional preview phase.
          </p>
          <p className="mt-3 text-sm text-gray-300">
            Requests are reviewed in onboarding waves to maintain operational quality and platform calibration.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-surface-4 bg-surface-1/70 p-4">
              <p className="text-xs text-gray-500">Assigned cohort</p>
              <p className="mt-1 text-sm font-medium">{submitted.cohort}</p>
            </div>
            <div className="rounded-lg border border-surface-4 bg-surface-1/70 p-4">
              <p className="text-xs text-gray-500">Onboarding wave</p>
              <p className="mt-1 text-sm font-medium">{submitted.onboardingWave}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 pb-16 pt-14 sm:px-6 lg:px-8">
      <p className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1 text-xs font-medium text-sky-200">
        <ShieldCheck className="h-3.5 w-3.5" /> {EARLY_ACCESS_BADGE.label} · {EARLY_ACCESS_BADGE.detail}
      </p>
      <h1 className="mt-5 text-4xl font-semibold tracking-tight">Request institutional early access</h1>
      <p className="mt-4 max-w-3xl text-gray-300">
        Navyrx Pulse is onboarding a limited number of users in controlled rollout waves to preserve trust, calibration quality, and operational discipline.
      </p>

      <div className="mt-6 grid gap-3 text-xs sm:grid-cols-3">
        <div className="rounded-lg border border-surface-4 bg-surface-1 p-3">
          <p className="text-gray-500">Rollout phase</p>
          <p className="mt-1 text-gray-200">{ACCESS_GOVERNANCE.phase}</p>
        </div>
        <div className="rounded-lg border border-surface-4 bg-surface-1 p-3">
          <p className="text-gray-500">Governance model</p>
          <p className="mt-1 text-gray-200">{ACCESS_GOVERNANCE.manualApproval ? 'Manual approval actively in use' : 'Automated approvals'}</p>
        </div>
        <div className="rounded-lg border border-surface-4 bg-surface-1 p-3">
          <p className="text-gray-500">Onboarding cadence</p>
          <p className="mt-1 text-gray-200">{ACCESS_GOVERNANCE.onboardingWave} review window</p>
        </div>
      </div>

      <form className="mt-8 rounded-2xl border border-surface-4 bg-surface-1 p-6" onSubmit={handleSubmit}>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Display Name</span>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required placeholder="How you appear to others" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Full Name</span>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} required placeholder="First and last name" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Primary Sport Interest</span>
            <select value={sportInterest} onChange={(e) => setSportInterest(e.target.value)} required className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2">
              <option value="">Select a sport</option>
              {SPORT_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Profile Type</span>
            <select value={userType} onChange={(e) => setUserType(e.target.value)} className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2">
              <option>Casual</option>
              <option>Serious bettor</option>
              <option>Analyst</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Betting Frequency</span>
            <select value={bettingFrequency} onChange={(e) => setBettingFrequency(e.target.value)} required className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2">
              <option value="">Select frequency</option>
              {BETTING_FREQUENCY_OPTIONS.map((f) => <option key={f}>{f}</option>)}
            </select>
          </label>
          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs text-gray-400">Why are you interested in Navyrx?</span>
            <textarea value={intent} onChange={(e) => setIntent(e.target.value)} required rows={3} placeholder="e.g. I want sharper line shopping and data-driven picks for NFL season" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs text-gray-400">Desired use case</span>
            <textarea value={useCase} onChange={(e) => setUseCase(e.target.value)} required rows={3} placeholder="e.g. Track player props across books, build a model for totals, manage unit sizing" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
        </div>

        <p className="mt-6 text-xs uppercase tracking-wide text-gray-500">Optional profile context</p>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Bankroll range</span>
            <input value={bankrollRange} onChange={(e) => setBankrollRange(e.target.value)} placeholder="e.g. $500–$2,000" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Professional background</span>
            <input value={professionalBackground} onChange={(e) => setProfessionalBackground(e.target.value)} placeholder="e.g. Software engineer, finance, media" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Favorite sportsbook</span>
            <input value={favoriteSportsbook} onChange={(e) => setFavoriteSportsbook(e.target.value)} placeholder="e.g. DraftKings, FanDuel, BetMGM" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Analytics sophistication</span>
            <input value={analyticsLevel} onChange={(e) => setAnalyticsLevel(e.target.value)} placeholder="e.g. Beginner, intermediate, builds models" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Preferred bet type</span>
            <input value={preferredBetType} onChange={(e) => setPreferredBetType(e.target.value)} placeholder="e.g. Moneylines, Props, Parlays" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-gray-400">Invite code (if provided)</span>
            <input value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder="e.g. NAVYRX-XXXX" className="w-full rounded-md border border-surface-4 bg-surface-2 px-3 py-2" />
          </label>
        </div>

        {/* Honeypot – visually hidden; bots fill it, humans do not */}
        <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden" tabIndex={-1}>
          <label htmlFor="_hp">Leave this field blank</label>
          <input
            id="_hp"
            name="_hp"
            type="text"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
          />
        </div>

        {error && (
          <p className="mt-4 rounded-md border border-loss/30 bg-loss/10 px-3 py-2 text-sm text-loss">{error}</p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button type="submit" className="cinematic-cta" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting…' : 'Submit Access Request'}
          </button>
          <p className="text-xs text-gray-500">
            Access is expanded gradually to maintain operational quality and platform calibration.
          </p>
        </div>

        <p className="mt-4 text-xs text-gray-500">
          By submitting this form you consent to Navyrx storing and using the information above solely to evaluate and process your access request. Your data is not sold or shared with third parties. See our{' '}
          <a href="/about" className="underline hover:text-gray-300" target="_blank" rel="noopener noreferrer">
            transparency policy
          </a>{' '}
          for details.
        </p>
      </form>
    </div>
  );
}
